import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, CalendarCheck, ChevronDown } from "lucide-react";
import { resolveMediaUrl } from "@/lib/api";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ hotel, onEnquire }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-end overflow-hidden bg-[#0A192F]" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={resolveMediaUrl(hotel?.hero_image)}
          alt="Lake Eufaula Inn"
          className="w-full h-[120%] object-cover"
          data-testid="hero-background-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/45 to-[#0A192F]/30" />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#0A192F]/70 to-transparent" />
      <div className="absolute inset-0 hero-grain" />

      <motion.div style={{ y: contentY, opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-32 pt-40 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="text-[#C5A059] text-xs sm:text-sm font-semibold uppercase tracking-[0.4em] mb-6"
          data-testid="hero-eyebrow"
        >
          {hotel?.tagline || "A Modern Lakeside Landmark"}
        </motion.p>

        <h1 className="font-serif-display font-bold text-[#F8F6F0] leading-[0.95] text-5xl sm:text-6xl lg:text-8xl tracking-tight" data-testid="hero-heading">
          <MaskedLine delay={0.15}>Where the Lake</MaskedLine>
          <MaskedLine delay={0.3}>Meets Southern</MaskedLine>
          <MaskedLine delay={0.45}>
            <span className="italic text-[#C5A059]">Grandeur.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-7 max-w-xl text-[#F8F6F0]/80 text-base sm:text-lg leading-relaxed"
          data-testid="hero-subtext"
        >
          {hotel?.intro}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={hotel?.phone_href || "tel:+13346870000"}
            className="group bg-[#F8F6F0] text-[#0A192F] hover:bg-[#C5A059] px-8 py-4 rounded-full font-semibold transition-all duration-300 shadow-xl flex items-center gap-3"
            data-testid="hero-call-to-book-button"
          >
            <Phone size={18} className="group-hover:rotate-12 transition-transform" />
            Call to Book — {hotel?.phone}
          </a>
          <button
            onClick={onEnquire}
            className="px-8 py-4 rounded-full font-medium text-[#F8F6F0] border border-[#F8F6F0]/35 hover:border-[#C5A059] hover:text-[#C5A059] transition-all duration-300 flex items-center gap-3 backdrop-blur-sm"
            data-testid="hero-enquire-button"
          >
            <CalendarCheck size={18} /> Enquire Online
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#F8F6F0]/60 z-10"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ChevronDown size={22} />
        </motion.div>
      </motion.div>
    </section>
  );
};
